/**
 * Seek-bar preview ("trickplay") thumbnail generation service.
 *
 * Sibling of subtitleService.js / dubService.js. Where those transcribe or dub,
 * this asks the analyze server to build the little scene previews that appear
 * when you hover/drag the player's seek bar: one SPRITE SHEET image (all sampled
 * frames tiled into a grid) plus a WebVTT index mapping each time window to a
 * rectangle in the sprite (see server/lib/spriteBuild.js).
 *
 * This is PURE ffmpeg on the server — no Gemini, no quota — but it reuses the
 * exact same analyze payload builder so File uploads and library/CDN videos are
 * resolved identically to every other AI action.
 *
 * Result: { vttUrl, spriteUrl, spriteUrls, interval, frameCount, tile }. The CMS
 * stores vttUrl (+ spriteUrl) on the content so the web-app player can load them.
 *
 * Requires REACT_APP_GEMINI_ANALYZE_URL to point at the analyze server.
 */

import axios from "axios";
import buildVideoAnalyzePayload, {
  VideoSourceError,
} from "./gemini/buildAnalyzeRequest";

const GEMINI_BASE_URL = process.env.REACT_APP_GEMINI_ANALYZE_URL || "";

export { VideoSourceError };

/** Stages surfaced to the UI while a thumbnail job runs (server `stage` ids map onto these). */
export const THUMBNAIL_STAGES = [
  { id: "uploading", label: "Uploading" },
  { id: "building_thumbnails", label: "Capturing frames" },
];

const wait = (ms, signal) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true }
    );
  });

const requireServer = () => {
  if (!GEMINI_BASE_URL) {
    throw new Error(
      "Thumbnail generation isn't configured. Set REACT_APP_GEMINI_ANALYZE_URL to the analyze server."
    );
  }
  return GEMINI_BASE_URL.replace(/\/$/, "");
};

/** Attach the sampling interval onto the analyze payload (multipart or JSON). */
const withThumbnailParams = (payload, { interval }) => {
  const val = Number(interval) > 0 ? String(Math.round(interval)) : "";
  if (payload.type === "multipart") {
    if (val) payload.body.append("interval", val);
    return payload;
  }
  return {
    ...payload,
    body: { ...payload.body, interval: val || null },
  };
};

const startThumbnailJob = async (payload) => {
  const url = `${requireServer()}/videos/generate-thumbnails`;
  const config =
    payload.type === "multipart"
      ? { headers: { "Content-Type": "multipart/form-data" } }
      : {};
  const response = await axios.post(url, payload.body, config);
  const data = response.data?.data ?? response.data;
  return data?.job_id || data?.jobId;
};

const fetchThumbnailStatus = async (jobId) => {
  const url = `${requireServer()}/videos/generate-thumbnails/${jobId}`;
  const response = await axios.get(url);
  return response.data?.data ?? response.data;
};

const pollThumbnailJob = async ({ jobId, onStage, signal }) => {
  const pollMs = 2000;

  while (true) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

    const status = await fetchThumbnailStatus(jobId);
    if (status?.stage) onStage?.(status.stage, status.progress ?? 0, status.note);

    if (status?.status === "failed") {
      throw new Error(status.error || "Thumbnail generation failed. Please try again.");
    }
    if (status?.status === "complete") {
      onStage?.("complete", 100);
      return status;
    }

    await wait(pollMs, signal);
  }
};

/**
 * Generate seek-bar preview thumbnails for a video.
 *
 * @param {object}   params
 * @param {object}   params.video       File or library/CDN video descriptor.
 * @param {number}   [params.interval]  seconds between sampled frames (default 10, server-side).
 * @param {function} [params.onStage]   (stageId, pct, note) => void progress callback.
 * @param {AbortSignal} [params.signal]
 * @returns {Promise<{ vttUrl:string, spriteUrl:string, spriteUrls:string[], interval:number, frameCount:number, tile:object }>}
 */
export const generateThumbnails = async ({
  video,
  interval,
  onStage,
  signal,
} = {}) => {
  const base = buildVideoAnalyzePayload(video);
  if (!base) throw new Error("No video selected for thumbnail generation.");

  const payload = withThumbnailParams(base, { interval });

  onStage?.(THUMBNAIL_STAGES[0].id, 0);

  const jobId = await startThumbnailJob(payload);
  if (!jobId) throw new Error("Thumbnail API did not return a job id.");

  const finished = await pollThumbnailJob({ jobId, onStage, signal });
  const result = finished.result;
  if (!result?.vttUrl) {
    throw new Error("Thumbnail job finished without producing a preview index.");
  }

  return {
    vttUrl: result.vttUrl,
    spriteUrl: result.spriteUrl,
    spriteUrls: result.spriteUrls || [],
    interval: result.interval,
    frameCount: result.frameCount,
    tile: result.tile,
  };
};

export default generateThumbnails;
